<template>
    <main class="flex-1 relative overflow-y-auto py-6 focus:outline-none" tabindex="0">
        <form @submit.prevent="saveIom">
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 px-5">
                <div class="md:flex md:items-center md:justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="py-0.5 text-2xl font-semibold text-gray-900">{{ pageTitle }}</h1>
                    </div>
                </div>
            </div>
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="mt-6 shadow sm:rounded-lg">
                    <loading :status="loading"/>
                    <div class="bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('IOM details') }}</h3>
                            <p class="mt-1 text-sm leading-5 text-gray-500">
                                {{ $t('Internal office memo details and recipients') }}.
                            </p>
                            <div v-if="iom.number" class="mt-4 rounded-md bg-gray-50 px-3 py-2">
                                <div class="text-xs font-semibold text-gray-500">{{ $t('Memo number') }}</div>
                                <div class="mt-0.5 text-sm font-semibold text-gray-900 break-words">{{ iom.number }}</div>
                                <div class="mt-1 text-xs text-gray-500">{{ $t('The memo number cannot be changed') }}.</div>
                            </div>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="grid grid-cols-3 gap-6">
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="subject">{{ $t('Subject') }}</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="subject"
                                            v-model="iom.subject"
                                            :placeholder="$t('Subject')"
                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                            required
                                        >
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="content">{{ $t('Content') }}</label>
                                    <input-wysiwyg
                                        id="content"
                                        v-model="iom.content"
                                    ></input-wysiwyg>
                                </div>
                                <div class="col-span-3 sm:col-span-1">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="memo_date">{{ $t('Memo date') }}</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="memo_date"
                                            v-model="iom.memo_date"
                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                            required
                                            type="date"
                                        >
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700">{{ $t('To') }} ({{ $t('Divisions') }})</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <svg-vue class="h-5 w-5 text-gray-400" icon="font-awesome.search-regular"></svg-vue>
                                        </div>
                                        <input
                                            v-model.trim="departmentSearch"
                                            :placeholder="$t('Search division')"
                                            class="form-input block w-full pl-10 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                            type="text"
                                        >
                                    </div>
                                    <div class="mt-2 max-h-48 overflow-y-auto rounded-lg border border-gray-200">
                                        <button
                                            v-for="department in filteredDepartments"
                                            :key="'iom-dept-option-' + department.id"
                                            class="w-full text-left px-3 py-2 hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                                            type="button"
                                            @click="addDepartment(department.id)"
                                        >
                                            <span class="text-sm text-gray-700 truncate">{{ department.name }}</span>
                                        </button>
                                        <div v-if="filteredDepartments.length === 0" class="px-3 py-2 text-sm text-gray-500">
                                            {{ $t('No divisions found') }}
                                        </div>
                                    </div>
                                    <div v-if="selectedDepartments.length > 0" class="mt-3">
                                        <div class="text-xs font-semibold text-gray-500 mb-2">{{ $t('Selected divisions') }}</div>
                                        <div class="flex flex-wrap gap-1">
                                            <span
                                                v-for="department in selectedDepartments"
                                                :key="'iom-dept-chip-' + department.id"
                                                class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                            >
                                                {{ department.name }}
                                                <button class="ml-2 text-gray-500 hover:text-red-600" type="button" @click="removeDepartment(department.id)">×</button>
                                            </span>
                                        </div>
                                    </div>
                                    <div v-else class="mt-2 text-xs text-gray-500">{{ $t('No divisions selected') }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="bg-gray-100 text-right px-4 py-3 sm:px-6">
                        <div class="inline-flex">
                            <router-link
                                class="btn btn-secondary shadow-sm rounded-md mr-4"
                                to="/dashboard/iom"
                            >
                                {{ $t('Cancel') }}
                            </router-link>
                            <button
                                class="btn btn-green shadow-sm rounded-md"
                                type="submit"
                            >
                                {{ submitLabel }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </main>
</template>

<script>
export default {
    name: "iom-form",
    props: {
        // uuid IOM yang sedang diedit; null berarti membuat memo baru.
        iomUuid: {
            type: String,
            default: null,
        },
    },
    metaInfo() {
        return {
            title: this.pageTitle
        }
    },
    data() {
        return {
            loading: false,
            departmentList: [],
            departmentSearch: '',
            iom: {
                number: null,
                subject: null,
                memo_date: null,
                content: '',
                department_ids: [],
            },
        }
    },
    computed: {
        isEdit() {
            return !!this.iomUuid;
        },
        pageTitle() {
            return this.isEdit ? this.$i18n.t('Edit IOM') : this.$i18n.t('Create IOM');
        },
        submitLabel() {
            return this.isEdit ? this.$i18n.t('Save IOM') : this.$i18n.t('Create IOM');
        },
        selectedDepartments() {
            return this.departmentList.filter((department) => {
                return this.iom.department_ids.indexOf(department.id) !== -1;
            });
        },
        filteredDepartments() {
            const self = this;
            const search = self.departmentSearch.toLowerCase();
            return self.departmentList.filter(function (department) {
                if (self.iom.department_ids.indexOf(department.id) !== -1) {
                    return false;
                }
                if (!search) {
                    return true;
                }
                return department.name.toLowerCase().indexOf(search) !== -1;
            });
        },
    },
    mounted() {
        this.getDepartments();
        if (this.isEdit) {
            this.getIom();
        } else {
            // Tanggal memo default hari ini, supaya staf tidak perlu mengisi manual.
            const today = new Date();
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const day = String(today.getDate()).padStart(2, '0');
            this.iom.memo_date = today.getFullYear() + '-' + month + '-' + day;
        }
    },
    methods: {
        getDepartments() {
            const self = this;
            axios.get('api/dashboard/iom/filters').then(function (response) {
                self.departmentList = response.data.departments || [];
            });
        },
        getIom() {
            const self = this;
            self.loading = true;
            axios.get('api/dashboard/iom/' + self.iomUuid).then(function (response) {
                self.iom.number = response.data.number;
                self.iom.subject = response.data.subject;
                self.iom.memo_date = response.data.memo_date;
                self.iom.content = response.data.content || '';
                self.iom.department_ids = response.data.department_ids || [];
                self.loading = false;
            }).catch(function () {
                self.loading = false;
            });
        },
        addDepartment(departmentId) {
            if (this.iom.department_ids.indexOf(departmentId) === -1) {
                this.iom.department_ids.push(departmentId);
            }
            this.departmentSearch = '';
        },
        removeDepartment(departmentId) {
            this.iom.department_ids = this.iom.department_ids.filter(function (id) {
                return id !== departmentId;
            });
        },
        saveIom() {
            const self = this;

            if (self.iom.department_ids.length === 0) {
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$i18n.t('Select at least one division').toString(),
                    type: 'error'
                });
                return;
            }

            self.loading = true;
            const payload = {
                subject: self.iom.subject,
                memo_date: self.iom.memo_date,
                content: self.iom.content,
                department_ids: self.iom.department_ids,
            };

            const request = self.isEdit
                ? axios.put('api/dashboard/iom/' + self.iomUuid, payload)
                : axios.post('api/dashboard/iom', payload);

            request.then(function (response) {
                self.loading = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t(self.isEdit ? 'Data updated correctly' : 'Data saved correctly').toString(),
                    type: 'success'
                });
                self.$router.push('/dashboard/iom/' + response.data.iom.uuid);
            }).catch(function () {
                self.loading = false;
            });
        },
    }
}
</script>
